import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import styles from "./Forms.module.css";

import TextInput from "../UILibrary/TextInput";
import Submission from "../UILibrary/Submission";
import RadioButton from "../UILibrary/RadioButton";

import { Email } from "Utils/Global/regularExpressions";
import { isValidIntlPhone } from "Utils/Global/phoneValidation";
import { UserAuthenticationStoreImplementation } from "Data/UserAuthentication/UserAuthenticationStoreImplementation";
import UserIcon from "Utils/Assets/SVGs/UserIcon";
import { defaultEditProfileValues } from "Utils/Global/globalConstants";
import type { IEditProfileFormData } from "@model/Authentication.model";
import { AuthenticationService } from "../../../Logic/Authentication/AuthenticationService";
import { useForceLogoutAndRedirect } from "../../../Logic/Functions/Hooks/useForceLogoutAndRedirect";

const ProfileForm: React.FC = () => {
  const userAuthStore = UserAuthenticationStoreImplementation();
  const user = userAuthStore?.authenticationState?.user;
  const token = userAuthStore?.authenticationState?.token ?? "";
  const navigate = useNavigate();
  const authService = new AuthenticationService();
  const { control, handleSubmit, reset } = useForm<IEditProfileFormData>({
    mode: "onSubmit",
    defaultValues: defaultEditProfileValues,
  });
  const onAuthError = useForceLogoutAndRedirect();

  const [apiError, setApiError] = useState<string | undefined>(undefined);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!user) return;

    reset({
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      email: user.email ?? "",
      phoneNumber: user.phoneNumber ?? "",
      accountType: user.role === "RESELLER" ? "reseller" : "client",
    });
  }, [user, reset]);

  const handleStartEdit = () => {
    setIsEditing(true);
    setSuccess(null);
    setApiError(undefined);
  };

  const handleCancelEdit = () => {
    if (!user) return;

    reset({
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      email: user.email ?? "",
      phoneNumber: user.phoneNumber ?? "",
      accountType: user.role === "RESELLER" ? "reseller" : "client",
    });
    setIsEditing(false);
    setApiError(undefined);
  };

  const onValidSubmit = async (form: IEditProfileFormData) => {
    setSuccess(null);

    const msg = await authService.handleEditProfile({
      data: form,
      token,
      onAuthError,
      reset,
      handleUserData: userAuthStore.logUserIn,
      setApiError: (m) => setApiError(m ?? undefined),
      setLoading,
    });

    if (msg) {
      setSuccess(msg);
      setIsEditing(false);
    }
  };

  if (!user) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.formContainerr}>
          <div className={styles.formInner}>
            <h2 className={styles.formTitle}>Profile</h2>
            <p className={styles.formText}>
              You need to be logged in to view or edit your profile.
            </p>

            <button
              type="button"
              className={styles.goBackButton}
              onClick={() => navigate("/auth/login")}
              data-testid="edit_profile_login_redirect"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.formContainerr}>
        <form
          className={`${styles.formInner} ${styles.editProfileFormInner}`}
          data-testid="edit_profile_form"
        >
          <div
            className={styles.verificationIconWrapper}
            data-testid="edit_profile_icon"
          >
            <UserIcon className={styles.profileIcon} />
          </div>

          <div className="margin-top--20">
            <h2 className={styles.formTitle} data-testid="edit_profile_title">
              Profile
            </h2>

            <div className="margin-top--10">
              <p className={styles.formText} data-testid="edit_profile_text">
                View your profile details. Click “Edit Profile” to update your
                information.
              </p>
            </div>
          </div>

          {success && (
            <div
              className={styles.successMessage}
              data-testid="edit_profile_success"
            >
              <p>{success}</p>
            </div>
          )}

          <div className={styles.nameFields}>
            <Controller
              name="firstName"
              control={control}
              rules={
                isEditing
                  ? { required: "First name is required" }
                  : undefined
              }
              render={({ field, fieldState }) => (
                <TextInput
                  label="First Name"
                  placeholder="Enter first name"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  disabled={!isEditing}
                />
              )}
            />

            <Controller
              name="lastName"
              control={control}
              rules={
                isEditing
                  ? { required: "Last name is required" }
                  : undefined
              }
              render={({ field, fieldState }) => (
                <TextInput
                  label="Last Name"
                  placeholder="Enter last name"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  disabled={!isEditing}
                />
              )}
            />
          </div>

          <Controller
            name="email"
            control={control}
            rules={
              isEditing
                ? {
                  required: { value: true, message: "Email is required!" },
                  pattern: {
                    value: Email,
                    message: "Invalid email pattern!",
                  },
                }
                : undefined
            }
            render={({ field, fieldState }) => (
              <TextInput
                label="Email"
                placeholder="Enter your email"
                type="email"
                value={field.value ?? ""}
                onChange={field.onChange}
                error={fieldState.error?.message}
                disabled={!isEditing}
              />
            )}
          />

          <div className={styles.formRow}>
            <div className={styles.formHalf}>
              <Controller
                name="phoneNumber"
                control={control}
                rules={
                  isEditing
                    ? {
                      required: "Phone is required",
                      validate: (value) =>
                        isValidIntlPhone(value) ||
                        "Invalid phone format. Use +country code.",
                    }
                    : undefined
                }
                render={({ field, fieldState }) => (
                  <TextInput
                    label="Phone"
                    placeholder="Enter your phone +961 ..."
                    type="tel"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                    disabled={!isEditing}
                  />
                )}
              />
            </div>

            <Controller
              name="accountType"
              control={control}
              rules={
                isEditing
                  ? { required: "Please select account type" }
                  : undefined
              }
              render={({ field, fieldState }) => (
                <RadioButton
                  label="Account Type"
                  options={[
                    { label: "Reseller", value: "reseller" },
                    { label: "Client", value: "client" },
                  ]}
                  defaultValue={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  disabled={!isEditing}
                />
              )}
            />
          </div>

          <div className={styles.editProfileActions}>
            {!isEditing && (
              <button
                type="button"
                className={styles.editProfileButton}
                onClick={handleStartEdit}
                data-testid="edit_profile_button"
              >
                Edit Profile
              </button>
            )}

            {isEditing && (
              <div className={styles.editProfileActionsRow}>
                <Submission
                  buttonText={loading ? "Saving..." : "Save Changes"}
                  onClick={handleSubmit(onValidSubmit)}
                  error={apiError}
                  disabled={loading}
                />

                <button
                  type="button"
                  className={styles.editProfileCancelButton}
                  onClick={handleCancelEdit}
                  data-testid="edit_profile_cancel"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileForm;
